import { useMemo } from 'react';
import useMeQuery from '@hooks/useMeQuery';
import { useConnectionsPageActionsApi } from '@metrics/client/react/useConnectionsPageActionsApi';
import { AnalyticPageId } from '../types';

interface SelfData {
  selfAttendeeId: string;
  selfContactId: string;
}

const useSelfData = (): SelfData => {
  const meData = useMeQuery()?.data?.me || {
    id: undefined,
    contactId: undefined
  };
  const { id: selfAttendeeId, contactId: selfContactId } = meData;

  return { selfAttendeeId, selfContactId };
};
interface ConnectionTrackFields {
  attendeeId: string;
  contactId: string;
  analyticPageId: AnalyticPageId;
}
type ConnectionTrackI = (fields: ConnectionTrackFields) => void;
type API = {
  request: ConnectionTrackI;
  accept: ConnectionTrackI;
  decline: ConnectionTrackI;
  cancel: ConnectionTrackI;
  remove: ConnectionTrackI;
};

const useConnectionActionAnalytics = (): API => {
  const {
    sendConnectionRequest,
    acceptConnectionRequest,
    declineConnectionRequest,
    cancelConnectionRequest,
    removeConnection
  } = useConnectionsPageActionsApi();
  const { selfAttendeeId, selfContactId } = useSelfData();

  return useMemo(
    () => ({
      request({ attendeeId, contactId, analyticPageId }: ConnectionTrackFields): void {
        sendConnectionRequest({
          initiatorContactId: selfContactId,
          initiatorAttendeeId: selfAttendeeId,
          recipientContactId: contactId,
          recipientAttendeeId: attendeeId,
          factOrigin: analyticPageId
        });
      },
      accept({ attendeeId, contactId, analyticPageId }: ConnectionTrackFields): void {
        acceptConnectionRequest({
          initiatorContactId: contactId,
          initiatorAttendeeId: attendeeId,
          recipientContactId: selfContactId,
          recipientAttendeeId: selfAttendeeId,
          factOrigin: analyticPageId
        });
      },
      decline({ attendeeId, contactId, analyticPageId }: ConnectionTrackFields): void {
        declineConnectionRequest({
          initiatorContactId: contactId,
          initiatorAttendeeId: attendeeId,
          recipientContactId: selfContactId,
          recipientAttendeeId: selfAttendeeId,
          factOrigin: analyticPageId
        });
      },
      cancel({ attendeeId, contactId, analyticPageId }: ConnectionTrackFields): void {
        cancelConnectionRequest({
          initiatorContactId: selfContactId,
          initiatorAttendeeId: selfAttendeeId,
          recipientContactId: contactId,
          recipientAttendeeId: attendeeId,
          factOrigin: analyticPageId
        });
      },
      remove({ attendeeId, contactId, analyticPageId }: ConnectionTrackFields): void {
        removeConnection({
          initiatorContactId: selfContactId,
          initiatorAttendeeId: selfAttendeeId,
          recipientContactId: contactId,
          recipientAttendeeId: attendeeId,
          factOrigin: analyticPageId
        });
      }
    }),
    [selfAttendeeId, selfContactId]
  );
};

export default useConnectionActionAnalytics;
